import { useCallback, useEffect, useMemo, useRef, useState } from 'react';

import { useHistory } from 'react-router-dom';
import { noop } from 'ui-components/default-props/noop';

// If we ever get to react-router v6, we will need to update this hook to implement their new `useBlocker` hook instead,
// because history.block() has been removed.
export const useNavigationBlocker = (shouldBlockNavigation) => {
  const history = useHistory();
  const [lastBlocked, setLastBlocked] = useState(undefined);

  const unblockRef = useRef(noop);

  const onLocationChange = useCallback(
    (location, action) => {
      if (shouldBlockNavigation({ nextLocation: location, historyAction: action })) {
        setLastBlocked({
          location,
          action,
        });
        return false;
      }

      setLastBlocked(undefined);
      return true;
    },
    [shouldBlockNavigation],
  );

  useEffect(() => {
    // history.block is weird and has been replaced in newer versions of react-router because of this.
    //
    // It takes a callback that will run everytime history.location is about to change, if that callback returns
    // false (explicitly), then it will stop the location from changing, otherwise navigation to the new location will
    // proceed.
    //
    // It returns a function to unblock navigation. Once navigation is blocked due to the given callback, it will stay
    // blocked for ANY future navigation attempts unless the unblock function is called.
    unblockRef.current = history.block(onLocationChange);

    // Make sure we unblock on unmount
    return () => {
      unblockRef?.current();
    };
  }, [history, onLocationChange]);

  const unblockNavigation = useCallback(() => {
    unblockRef.current();
    setLastBlocked(undefined);
  }, []);

  const continueLastNavigation = useCallback(() => {
    if (!lastBlocked) {
      return;
    }

    const { location, action } = lastBlocked;
    setLastBlocked(undefined);
    switch (action) {
      case 'PUSH':
        history.push(location);
        break;
      case 'REPLACE':
        history.replace(location);
        break;
      case 'POP':
      default:
        history.goBack();
        break;
    }
  }, [history, lastBlocked]);

  return useMemo(() => {
    return {
      unblockNavigation,
      continueLastNavigation,
    };
  }, [unblockNavigation, continueLastNavigation]);
};
